import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Default Leaflet marker icons break under bundlers; load from CDN.
const clubIcon = new L.Icon({
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

const userIcon = L.divIcon({
  className: "",
  html: '<div style="width:18px;height:18px;background:#D94A3D;border:3px solid #fff;border-radius:50%;box-shadow:0 0 0 2px #17324D"></div>',
  iconSize: [18, 18], iconAnchor: [9, 9],
});

const esc = (s) => String(s ?? "").replace(/[&<>"]/g, (m) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[m]));

export default function ClubMap({ clubs = [], userLoc, center }) {
  const ref = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    if (mapRef.current || !ref.current) return;
    const map = L.map(ref.current, { scrollWheelZoom: true }).setView(center || [39.9526, -75.1652], 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; layerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Draw markers whenever clubs or user location change.
  useEffect(() => {
    const map = mapRef.current, layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const pts = [];
    clubs.forEach((c) => {
      if (typeof c.lat !== "number" || typeof c.lng !== "number") return;
      const m = L.marker([c.lat, c.lng], { icon: clubIcon }).addTo(layer);
      m.bindPopup(
        `<div style="min-width:160px">
          <b style="font-size:14px">${esc(c.name)}</b><br>
          <span style="color:#17324D;font-weight:700">$${esc(c.monthly_price)}/mo</span>
          ${c.city ? `<br><span style="color:#78716C">${esc(c.city)}</span>` : ""}
          <br><a href="/clubs/${esc(c.id)}" style="color:#D94A3D;font-weight:600">View &amp; Join →</a>
        </div>`
      );
      pts.push([c.lat, c.lng]);
    });
    if (userLoc) {
      L.marker([userLoc.lat, userLoc.lng], { icon: userIcon }).addTo(layer).bindPopup("You are here");
      pts.push([userLoc.lat, userLoc.lng]);
    }
    if (pts.length === 1) map.setView(pts[0], 13);
    else if (pts.length > 1) map.fitBounds(pts, { padding: [40, 40], maxZoom: 14 });
    setTimeout(() => map.invalidateSize(), 100);
  }, [clubs, userLoc]);

  // Recenter on an explicit center change (e.g. ZIP search with no matching clubs).
  useEffect(() => {
    if (mapRef.current && center) mapRef.current.setView(center, 12);
  }, [center]);

  return (
    <div
      ref={ref}
      data-testid="club-map"
      className="rounded-xl border border-[#17324D]/15 overflow-hidden relative z-0 h-[560px] w-full"
    />
  );
}
